import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaMapMarkerAlt, FaClock, FaUsers, FaDollarSign, FaSpinner,
  FaSeedling, FaHardHat, FaIndustry, FaHome, FaBolt, FaTruck,
  FaShoppingCart, FaUmbrellaBeach, FaUniversity, FaHospital,
  FaGraduationCap, FaLaptop, FaFilm, FaBriefcase, FaLandmark,
  FaPalette, FaFutbol, FaLeaf, FaPlane, FaHandshake
} from 'react-icons/fa';
import { listingService } from '../services/listingService';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FiltersSection = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-weight: 500;
  margin-bottom: 0.3rem;
  color: ${props => props.theme.colors.text};
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.4rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.35rem;
  }
`;

const VenturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const VentureCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 20px ${props => props.theme.colors.shadowHover};
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const VentureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #8B5A96, #A569BD)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const VentureTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const VentureDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const VentureDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #8B5A96;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ResetButton = styled.button`
  background-color: #8B5A96;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #6B2E7A;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.75rem;
  }
`;

// Mock data for demonstration
const mockVentures = [
  {
    id: 1,
    title: "Children's Clothing Business",
    description: "We manufacture and sell children's clothing in Cape Town",
    age: "2 years ago",
    size: "2",
    industry: "Retail & Wholesale Trade",
    role: "Partner",
    skills: ["Finance & Accounting", "Marketing & Sales", "Writing & Content Creation"],
    timeCommitment: "10-40",
    pay: "Commission",
    location: "Cape Town",
    remote: "Hybrid",
    fullStory: "Two years ago my mother and I saw a gap in the market with kids clothing. We make clothing that is stretchy and kids can grow into it as they keep growing..."
  },
  {
    id: 2,
    title: "Organic Food Delivery",
    description: "Farm-to-table organic produce delivery service",
    age: "6 months ago",
    size: "3",
    industry: "Retail & Wholesale Trade",
    role: "Employee",
    skills: ["Logistics & Supply Chain", "Customer Service & Support"],
    timeCommitment: "20-30",
    pay: "Monthly",
    location: "Johannesburg",
    remote: "On-site",
    fullStory: "Started 6 months ago to bridge the gap between organic farmers and health-conscious consumers..."
  },
  {
    id: 3,
    title: "Tech Startup",
    description: "Mobile app development for local businesses",
    age: "1 year ago",
    size: "4",
    industry: "Technology & IT",
    role: "Contract",
    skills: ["Technology & IT", "Design & Creative Arts"],
    timeCommitment: "40+",
    pay: "Hourly",
    location: "Cape Town",
    remote: "Remote",
    fullStory: "We're building innovative mobile solutions for small businesses in South Africa..."
  },
  {
    id: 4,
    title: "Event Planning Service",
    description: "Wedding and corporate event planning",
    age: "3 years ago",
    size: "5",
    industry: "Professional & Business Services",
    role: "Part-time",
    skills: ["Project Management", "Communication & Public Relations"],
    timeCommitment: "10-20",
    pay: "Commission",
    location: "Durban",
    remote: "Hybrid",
    fullStory: "Established event planning company looking to expand our team with creative professionals..."
  }
];

const industries = [
  "All Industries",
  "Agriculture & Forestry",
  "Mining & Natural Resources",
  "Manufacturing",
  "Construction & Real Estate",
  "Utilities & Energy",
  "Transportation & Logistics",
  "Retail & Wholesale Trade",
  "Hospitality & Tourism",
  "Financial Services",
  "Healthcare & Life Sciences",
  "Education & Training",
  "Information & Communication Technology (ICT)",
  "Media & Entertainment",
  "Professional & Business Services",
  "Public Sector & Non-Profit",
  "Arts, Culture & Creative Industries",
  "Sports & Recreation",
  "Environmental & Sustainability",
  "Aerospace & Defense",
  "Consumer Services"
];

// Industry icon mapping
const getIndustryIcon = (industry) => {
  const iconMap = {
    'Agriculture & Forestry': { icon: FaSeedling, gradient: 'linear-gradient(135deg, #4CAF50, #81C784)' },
    'Mining & Natural Resources': { icon: FaHardHat, gradient: 'linear-gradient(135deg, #795548, #A1887F)' },
    'Manufacturing': { icon: FaIndustry, gradient: 'linear-gradient(135deg, #607D8B, #90A4AE)' },
    'Construction & Real Estate': { icon: FaHome, gradient: 'linear-gradient(135deg, #FF9800, #FFB74D)' },
    'Utilities & Energy': { icon: FaBolt, gradient: 'linear-gradient(135deg, #FFC107, #FFD54F)' },
    'Transportation & Logistics': { icon: FaTruck, gradient: 'linear-gradient(135deg, #3F51B5, #7986CB)' },
    'Retail & Wholesale Trade': { icon: FaShoppingCart, gradient: 'linear-gradient(135deg, #E91E63, #F06292)' },
    'Hospitality & Tourism': { icon: FaUmbrellaBeach, gradient: 'linear-gradient(135deg, #00BCD4, #4DD0E1)' },
    'Financial Services': { icon: FaUniversity, gradient: 'linear-gradient(135deg, #4CAF50, #66BB6A)' },
    'Healthcare & Life Sciences': { icon: FaHospital, gradient: 'linear-gradient(135deg, #F44336, #EF5350)' },
    'Education & Training': { icon: FaGraduationCap, gradient: 'linear-gradient(135deg, #9C27B0, #BA68C8)' },
    'Information & Communication Technology (ICT)': { icon: FaLaptop, gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)' },
    'Technology & IT': { icon: FaLaptop, gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)' },
    'Media & Entertainment': { icon: FaFilm, gradient: 'linear-gradient(135deg, #673AB7, #9575CD)' },
    'Professional & Business Services': { icon: FaBriefcase, gradient: 'linear-gradient(135deg, #455A64, #78909C)' },
    'Public Sector & Non-Profit': { icon: FaLandmark, gradient: 'linear-gradient(135deg, #5D4037, #8D6E63)' },
    'Arts, Culture & Creative Industries': { icon: FaPalette, gradient: 'linear-gradient(135deg, #FF5722, #FF8A65)' },
    'Sports & Recreation': { icon: FaFutbol, gradient: 'linear-gradient(135deg, #8BC34A, #AED581)' },
    'Environmental & Sustainability': { icon: FaLeaf, gradient: 'linear-gradient(135deg, #009688, #4DB6AC)' },
    'Aerospace & Defense': { icon: FaPlane, gradient: 'linear-gradient(135deg, #3F51B5, #5C6BC0)' },
    'Consumer Services': { icon: FaHandshake, gradient: 'linear-gradient(135deg, #FF9800, #FFAB40)' }
  };
  
  return iconMap[industry] || { icon: FaUsers, gradient: 'linear-gradient(135deg, #8B5A96, #A569BD)' };
};

// Helper function to capitalize values from database
const capitalizeValue = (value) => {
  if (!value || value === 'Not specified') return value;
  // Handle hyphenated values (e.g., 'on-site' -> 'On-site')
  return value
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
};

const skills = [
  "All Skills",
  "Agriculture & Farming",
  "Communication & Public Relations",
  "Construction & Trades",
  "Customer Service & Support",
  "Data & Analytics",
  "Design & Creative Arts",
  "Education & Training",
  "Engineering & Technical",
  "Environmental & Sustainability",
  "Finance & Accounting",
  "Healthcare & Medical",
  "Hospitality & Tourism",
  "Human Resources & People Management",
  "Legal & Compliance",
  "Logistics & Supply Chain",
  "Manufacturing & Production",
  "Marketing & Sales",
  "Media & Entertainment",
  "Personal Services & Lifestyle",
  "Project Management",
  "Science & Research",
  "Sports & Fitness",
  "Technology & IT",
  "Writing & Content Creation"
];

const FindHustle = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    location: '',
    traveling: '',
    age: '',
    size: '',
    industry: '',
    role: '',
    skills: '',
    timeCommitment: '',
    pay: '',
    remote: ''
  });

  useEffect(() => {
    loadListings();
  }, [filters]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const filterParams = {};
      
      // Build filter parameters
      if (filters.industry && filters.industry !== 'All Industries') {
        filterParams.industry = filters.industry;
      }
      if (filters.remote && filters.remote !== 'Any Structure') {
        filterParams.remote = filters.remote;
      }
      if (filters.role && filters.role !== 'Any Role') {
        filterParams.role = filters.role;
      }
      if (filters.skills && filters.skills !== 'All Skills') {
        filterParams.skills = [filters.skills];
      }
      if (filters.location && filters.location !== 'All Locations') {
        filterParams.location = filters.location;
      }

      const { listings: fetchedListings } = await listingService.getListings(
        { ...filterParams, type: 'hustle' },
        20,
        0
      );
      
      setListings(fetchedListings || []);
    } catch (error) {
      console.error('Error loading listings:', error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      location: '',
      traveling: '',
      age: '',
      size: '',
      industry: '',
      role: '',
      skills: '',
      timeCommitment: '',
      pay: '',
      remote: ''
    });
  };

  const handleVentureClick = (listing) => {
    navigate(`/venture/${listing.id}`, { state: { listing } });
  };

  return (
    <Container>
      <FiltersSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, color: '#8B5A96', fontSize: '1.5rem' }}>Find Your Perfect Hustle</h2>
          <ResetButton onClick={handleResetFilters}>Reset Filters</ResetButton>
        </div>
        
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Location</FilterLabel>
            <FilterSelect value={filters.location} onChange={(e) => handleFilterChange('location', e.target.value)}>
              <option value="">All Locations</option>
              <option value="Cape Town">Cape Town</option>
              <option value="Johannesburg">Johannesburg</option>
              <option value="Durban">Durban</option>
              <option value="Pretoria">Pretoria</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Traveling Required</FilterLabel>
            <FilterSelect value={filters.traveling} onChange={(e) => handleFilterChange('traveling', e.target.value)}>
              <option value="">Any</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Business Age</FilterLabel>
            <FilterSelect value={filters.age} onChange={(e) => handleFilterChange('age', e.target.value)}>
              <option value="">Any Age</option>
              <option value="0-1">0-1 years</option>
              <option value="1-2">1-2 years</option>
              <option value="2-3">2-3 years</option>
              <option value="3-5">3-5 years</option>
              <option value="5+">5+ years</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Team Size</FilterLabel>
            <FilterSelect value={filters.size} onChange={(e) => handleFilterChange('size', e.target.value)}>
              <option value="">Any Size</option>
              <option value="1">1 person</option>
              <option value="2">2 people</option>
              <option value="3-5">3-5 people</option>
              <option value="5-10">5-10 people</option>
              <option value="10+">10+ people</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Industry</FilterLabel>
            <FilterSelect value={filters.industry} onChange={(e) => handleFilterChange('industry', e.target.value)}>
              {industries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Role Type</FilterLabel>
            <FilterSelect value={filters.role} onChange={(e) => handleFilterChange('role', e.target.value)}>
              <option value="">Any Role</option>
              <option value="Partner">Partner</option>
              <option value="Employee">Full-time Employee</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract Basis</option>
              <option value="Few hours">Few Hours a Week</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Skills Needed</FilterLabel>
            <FilterSelect value={filters.skills} onChange={(e) => handleFilterChange('skills', e.target.value)}>
              {skills.map(skill => (
                <option key={skill} value={skill}>{skill}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Time Commitment</FilterLabel>
            <FilterSelect value={filters.timeCommitment} onChange={(e) => handleFilterChange('timeCommitment', e.target.value)}>
              <option value="">Any Hours</option>
              <option value="1-10">1-10 hours/week</option>
              <option value="10-40">10-40 hours/week</option>
              <option value="40+">40+ hours/week</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Payment Structure</FilterLabel>
            <FilterSelect value={filters.pay} onChange={(e) => handleFilterChange('pay', e.target.value)}>
              <option value="">Any Payment</option>
              <option value="Hourly">Hourly</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Commission">Commission</option>
              <option value="Partnership">Partnership</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Work Structure</FilterLabel>
            <FilterSelect value={filters.remote} onChange={(e) => handleFilterChange('remote', e.target.value)}>
              <option value="">Any Structure</option>
              <option value="On-site">On-site</option>
              <option value="Hybrid">Hybrid</option>
              <option value="Remote">Remote</option>
            </FilterSelect>
          </FilterGroup>
        </FiltersGrid>
      </FiltersSection>

      {loading ? (
        <LoadingContainer>
          <FaSpinner size={24} />
          <span style={{ marginLeft: '1rem' }}>Loading ventures...</span>
        </LoadingContainer>
      ) : (
        <>
          <VenturesGrid>
            {listings.map(listing => {
              const { icon: IconComponent, gradient } = getIndustryIcon(listing.industry);
              return (
              <VentureCard key={listing.id} onClick={() => handleVentureClick(listing)}>
                <VentureIcon $gradient={gradient}>
                  <IconComponent />
                </VentureIcon>
                
                <VentureTitle>{listing.title}</VentureTitle>
                <VentureDescription>{listing.short_description}</VentureDescription>
                
                <VentureDetails>
                  <DetailItem>
                    <FaMapMarkerAlt />
                    <span>
                      Location: {(() => {
                        const loc = listing.location;
                        if (!loc?.province) return 'Not specified';
                        const displayArea = loc.area === 'Other' ? loc.customArea : loc.area;
                        return displayArea ? `${displayArea}, ${loc.province}` : loc.province;
                      })()}
                    </span>
                  </DetailItem>
                  <DetailItem>
                    <FaClock />
                    <span>Age: {listing.age || 'Not specified'} years</span>
                  </DetailItem>
                  <DetailItem>
                    <FaUsers />
                    <span>Team size: {listing.size || 'Not specified'} {listing.size === '1' ? 'person' : 'people'}</span>
                  </DetailItem>
                  <DetailItem>
                    <FaDollarSign />
                    <span>Pay: {capitalizeValue(listing.pay) || 'Not specified'}</span>
                  </DetailItem>
                  <DetailItem>
                    <span>
                      Skills: {listing.skills?.slice(0, 2).map(capitalizeValue).join(', ')}
                      {listing.skills?.length > 2 ? '...' : ''}
                    </span>
                  </DetailItem>
                  <DetailItem>
                    <span>Role: {capitalizeValue(listing.role) || 'Not specified'}</span>
                  </DetailItem>
                  <DetailItem>
                    <span>Work structure: {capitalizeValue(listing.remote) || 'Not specified'}</span>
                  </DetailItem>
                </VentureDetails>
              </VentureCard>
            );
            })}
          </VenturesGrid>

          {!loading && listings.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <h3>No ventures match your current filters</h3>
              <p>Try adjusting your filters to see more opportunities, or check back later for new listings</p>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default FindHustle;